import React, { useState, useRef, useEffect } from "react";
import { RSButton } from "reactsymbols-kit";
import Styles from "../../styles/colors";
import Modal from "../Modal";
import MethodSelect from "../MethodSelect";
import {
  useTransitionForStates,
  useMethodsForTransition,
  useController,
  useAsynchronousAction,
  shouldAutoCancelAddTransition,
} from "../../hooks";
import AddOrEditTransitionController from "../../merode/controllers/AddOrEditTransitionController";
import type { IModelVersion as IControllerModelVersion } from "../../merode/controllers/Controller";
import type { IModelVersion } from "../../stores/projects/model_version";
import type { IModel } from "../../stores/model";
import type { IObjectType } from "../../stores/model/metamodel/object_type";
import type { IFSM } from "../../stores/model/metamodel/fsm";
import type Report from "../../merode/checks/Report";
import { rsValue } from "../Inspector/rsForm";

/**
 * A typed controller method, re-typed to the loose action shape
 * `useAsynchronousAction` declares (see {@link AddStateForm} for the rationale).
 */
type AsyncActionLike = (...args: unknown[]) => Promise<unknown>;

interface AddTransitionFormProps {
  /** The ModelVersion to add the transition to. */
  modelVersion: IModelVersion;
  /** The ObjectType of the FSM to add the transition to. */
  objectType: IObjectType;
  /** The FSM to add the transition to. */
  fsm: IFSM;
  /** The id of the source state for the new transition. */
  source: string | null;
  /** The id of the target state for the new transition. */
  target: string | null;
  /** Callback to cancel adding a transition. */
  onCancel: () => void;
  /** Callback to send the report up. */
  onReport?: (report: Report) => void;
  /** Ref holding the enter-key handler, wired up by the parent modal. */
  onEnterHandler: React.MutableRefObject<(() => void) | undefined>;
}

/**
 * Form to add an FSM transition
 *
 * Props:
 * - modelVersion: the ModelVersion to add the transition to.
 * - objectType: the ObjectType of the FSM to add the transition to.
 * - fsm: the FSM to add the transition to.
 * - source: the source state for the new transition.
 * - target: the target state for the new transition.
 * - onCancel: callback to cancel adding a transition.
 * - onReport: callback to send the report up.
 * - onEnterHandler: ref to
 */
const AddTransitionForm = ({
  modelVersion,
  objectType,
  fsm,
  source,
  target,
  onCancel,
  onReport,
  onEnterHandler
}: AddTransitionFormProps) => {
  // The controller forwards `objectType`/`fsm` to the transition commands, whose
  // params accept `OrId` (instance or id); the constructor over-narrows them to
  // `string`. The store `IModelVersion` is the same loaded node the controller's
  // `IModelVersion` describes. Cast at this construction boundary only.
  const controller = useController(
    () =>
      new AddOrEditTransitionController(
        modelVersion as unknown as IControllerModelVersion,
        objectType as unknown as string,
        fsm as unknown as string
      )
  );
  // `modelVersion.content` is the store `IModel | null`; the hooks want the
  // `IModel` interface and a `string` state id. The version is loaded (content
  // non-null) and `source`/`target` are state ids here, matching the original
  // JS. Cast across the two `IModel` surfaces and the `string | null` ids.
  const transition = useTransitionForStates(
    modelVersion.content as unknown as IModel,
    source as string,
    target as string
  );
  const possibleMethods = useMethodsForTransition(
    modelVersion.content as unknown as IModel,
    objectType as unknown as string,
    source as string,
    target as string
  );
  const [methods, setMethods] = useState(
    transition != null ? transition.metaMethods.map(m => m.id) : []
  );

  // Close a live add that has nothing to pick. Must not re-fire after Cancel
  // has already cleared source/target while react-modal keeps this form mounted.
  useEffect(() => {
    if (
      shouldAutoCancelAddTransition({
        source,
        target,
        possibleMethodCount: possibleMethods.length,
      })
    ) {
      onCancel();
    }
  }, [possibleMethods, source, target, onCancel]);

  const valid = methods.length > 0;
  const busy = false;
  // The two branch actions carry concrete arg types; the hook accepts the loose
  // `(...args: unknown[]) => Promise<unknown>` shape. `source`/`target` flow as
  // the same `string | null` ids the original JS passed (the transition commands
  // accept `OrId`). `onReport` receives the resolved `Report` from the action;
  // the hook types the success arg as `unknown`, so re-type the callback to the
  // loose shape too. Casts are type-level only — runtime is unchanged.
  const addTransitionAction = useAsynchronousAction(
    (transition == null
      ? (methods: string[]) => controller.addTransition(source as string, target as string, methods)
      : (methods: string[]) => controller.editTransition(transition.id, methods)) as AsyncActionLike,
    onReport as ((...results: unknown[]) => void) | undefined
  );

  onEnterHandler.current = () => {
    if (valid && !busy) {
      addTransitionAction.execute(methods);
    }
  };

  return (
    <div className="merlin-modal add-transition-form">
      <div className="title">Add transition</div>
      <div className="modal-body">
        <div className="container">
          <div className="row">
            <div className="col">
              <MethodSelect
                methods={possibleMethods}
                selected={methods}
                onChange={setMethods}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="button-wrapper">
        <RSButton
          value="Cancel"
          type="button"
          background={Styles.defaultGradient}
          color={Styles.darkTextColor}
          onClick={onCancel}
        />
        <RSButton
          value={"Add"}
          type="submit"
          background={Styles.blueGradient}
          disabled={!valid || busy}
          onClick={() => addTransitionAction.execute(methods)}
          iconName={rsValue(busy ? "FaSpinner" : null)}
          iconSize={15}
          className={rsValue(busy ? "spin" : null)}
        />
      </div>
    </div>
  );
};

/** Props for {@link AddTransitionModal}: the form props (minus the internal
 * enter-handler ref) plus the modal open flag. */
interface AddTransitionModalProps extends Omit<AddTransitionFormProps, 'onEnterHandler'> {
  /** Is the modal open? */
  isOpen: boolean;
}

/**
 * Wrap the add transition form in a modal.
 *
 * Props:
 * - isOpen: is the modal open?
 * - onCancel: cancel adding a transition.
 * - See form above.
 */
const AddTransitionModal = ({ isOpen, onCancel, ...rest }: AddTransitionModalProps) => {
  const onEnterHandler = useRef<(() => void) | undefined>(undefined);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCancel}
      onEnter={() => {
        if (onEnterHandler.current != null) {
          onEnterHandler.current();
        }
      }}
    >
      {isOpen ? (
        <AddTransitionForm
          onCancel={onCancel}
          {...rest}
          onEnterHandler={onEnterHandler}
        />
      ) : null}
    </Modal>
  );
};

export default AddTransitionModal;
